import clsx from 'clsx'

import { OpenNew } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { Tag } from '@/components/tag/Tag'
import SectionHeader from '@/settings/components/SectionHeader'

// Marketing pricing page, matching PricingInformationLink. The design renders this as a plain button
// (not a link), so we navigate on click rather than reusing that anchor component.
const kBrowseAllPlansUrl = 'https://www.paxton.ai/pricing'

const kNameTextClass = 'font-rethink-sans text-base font-semibold leading-7 text-brand-neutral-950'
const kPriceTextClass = 'text-sm tracking-[-0.15px] text-brand-neutral-800'

type SubscriptionDetailsCardProps = {
  /** Plan label shown in bold, e.g. "Individual plan" / "Organization plan" / an account's status. */
  planName: string
  /** Pre-formatted recurring price, e.g. "$499 / month". Omit for accounts with no billing price. */
  planPrice?: string
  /** Struck-through standard price shown beside the discounted {@link planPrice} (lifetime % off only). */
  originalPrice?: string
  /** Discount badge shown next to the plan name, e.g. "50% OFF" (lifetime % off only). */
  discountTag?: string
  /** Promotion disclaimer for non-lifetime discounts, e.g. "Price shown before promotions". */
  disclaimer?: string
  /** When provided, renders the Manage Subscription button; omit to hide it. */
  onManageSubscription?: () => void
}

/**
 * The "Subscription Details" card: a header with a Browse all plans link, then a row with the plan
 * name + price on the left and a Manage Subscription button on the right. Shared by the individual
 * SubscriptionPanel and the org-admin BillingPanel so the two render identically to the same Figma
 * card. Presentational only — callers pass already-formatted strings.
 *
 * Price layouts (per Figma "Individual subscription use cases"):
 * - Default / org: plan name + price inline.
 * - Lifetime % off: plan name + {@link discountTag}, then {@link originalPrice} (struck) + {@link planPrice}.
 * - Other discounts: plan name + price* inline, with {@link disclaimer} below.
 * - No billing (exempt accounts): plan name only, no price.
 */
export function SubscriptionDetailsCard({ planName, planPrice, originalPrice, discountTag, disclaimer, onManageSubscription }: SubscriptionDetailsCardProps) {
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-brand-neutral-200 bg-white">
      <SectionHeader
        title="Subscription Details"
        trailing={
          <Button
            variant="plain"
            color="neutral"
            size="xs"
            trailingIcon={<OpenNew className="size-4" aria-hidden />}
            onClick={() => window.open(kBrowseAllPlansUrl, '_blank', 'noopener,noreferrer')}
          >
            Browse all plans
          </Button>
        }
      />
      <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex min-w-0 items-center gap-2">
            <span className={kNameTextClass}>{planName}</span>
            {discountTag && (
              <Tag variant="tonal" color="Neutral">
                {discountTag}
              </Tag>
            )}
            {/* Price sits inline with the name unless a struck original pushes it to its own line below. */}
            {!originalPrice && planPrice && <span className={kPriceTextClass}>{disclaimer ? `${planPrice}*` : planPrice}</span>}
          </div>
          {originalPrice && (
            <div className="flex items-center gap-2">
              <span className={clsx(kPriceTextClass, 'text-brand-neutral-500 line-through')}>{originalPrice}</span>
              {planPrice && <span className={kPriceTextClass}>{planPrice}</span>}
            </div>
          )}
          {disclaimer && <span className="text-xs text-brand-neutral-600">* {disclaimer}</span>}
        </div>
        {onManageSubscription && (
          <Button onClick={onManageSubscription} variant="tonal" color="neutral" size="sm" className="self-start sm:self-auto">
            Manage Subscription
          </Button>
        )}
      </div>
    </div>
  )
}
